import { useEffect, useRef } from "react";
import { Link } from "@tanstack/react-router";
import { trackEvent } from "@/lib/analytics";


type CTA = { label: string; to?: string; href?: string };

type Props = {
  /** Image background — URL absolue (unsplash) ou import asset */
  image: string;
  /** Petit eyebrow en haut */
  eyebrow?: string;
  /** Titre principal (peut contenir <span class="italic"> via JSX dans children) */
  title: React.ReactNode;
  /** Sous-titre court — 1 à 2 phrases max, pas de paragraphe SEO */
  subtitle?: React.ReactNode;
  /** CTA principal (or) */
  primary?: CTA;
  /** CTA secondaire (outline) */
  secondary?: CTA;
  /** Hauteur du hero */
  height?: "tall" | "regular" | "compact";
  /** Position horizontale du contenu */
  align?: "left" | "center";
  /** Désactive l'effet ken burns / parallax léger */
  staticBg?: boolean;
};

/**
 * PageHero — hero immersif pour toutes les pages internes.
 * Image plein écran + double overlay sombre (lisibilité garantie) +
 * accent or, parallax léger desktop (translateY au scroll), titre serif.
 */
export function PageHero({
  image,
  eyebrow,
  title,
  subtitle,
  primary,
  secondary,
  height = "regular",
  align = "center",
  staticBg = false,
}: Props) {
  const bgRef = useRef<HTMLDivElement | null>(null);

  // Parallax léger (desktop uniquement, sans lib)
  useEffect(() => {
    if (staticBg) return;
    if (typeof window === "undefined") return;
    if (window.matchMedia("(max-width: 768px)").matches) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const el = bgRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        // Translate background up to 80px max
        const y = Math.max(-120, Math.min(0, rect.top * 0.18));
        el.style.transform = `translate3d(0, ${y}px, 0) scale(1.08)`;
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [staticBg]);

  const heightCls =
    height === "tall"
      ? "min-h-[88vh]"
      : height === "compact"
      ? "min-h-[52vh] sm:min-h-[58vh]"
      : "min-h-[68vh] sm:min-h-[72vh]";

  const alignCls = align === "left" ? "items-start text-left" : "items-center text-center";
  const innerAlign = align === "left" ? "max-w-3xl" : "max-w-4xl mx-auto";

  return (
    <section className={`relative w-full ${heightCls} overflow-hidden bg-[#0a0a0a] on-dark`}>
      {/* Image background avec ken-burns/parallax */}
      <div
        ref={bgRef}
        className={`absolute inset-0 will-change-transform ${staticBg ? "" : "kenburns-img"}`}
        style={{
          backgroundImage: `url(${image})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          transform: "scale(1.08)",
        }}
        aria-hidden
      />
      {/* Overlay multi-couches : noir bas + vignette + touche or chaude */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(10,10,10,0.55) 0%, rgba(10,10,10,0.35) 35%, rgba(10,10,10,0.78) 100%)",
        }}
        aria-hidden
      />
      <div
        className="absolute inset-0 mix-blend-soft-light opacity-60"
        style={{
          background:
            "radial-gradient(ellipse at 50% 40%, rgba(255,200,87,0.35) 0%, transparent 65%)",
        }}
        aria-hidden
      />
      {/* Grain léger pour le côté premium */}
      <div
        className="absolute inset-0 opacity-[0.08] pointer-events-none mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.7'/></svg>\")",
        }}
        aria-hidden
      />

      {/* Contenu — centré verticalement, laisse la place à l'indicateur scroll en bas */}
      <div
        className={`relative z-10 flex ${alignCls} justify-center h-full min-h-[inherit] pt-28 pb-28 px-6 lg:px-10`}
        style={{ minHeight: "inherit" }}
      >
        <div className={`flex flex-col justify-center w-full ${innerAlign}`}>
          {eyebrow && (
            <p className="text-[10px] sm:text-[11px] uppercase tracking-[0.4em] text-[var(--gold)] mb-5">
              {eyebrow}
            </p>
          )}
          <h1 className="font-serif-display text-3xl sm:text-4xl md:text-5xl leading-[1.05] text-white">
            {title}
          </h1>
          <span className={`block h-px bg-[var(--gold)] w-16 mt-7 ${align === "center" ? "mx-auto" : ""}`} />
          {subtitle && (
            <p className={`mt-6 text-base sm:text-lg text-white/85 font-light leading-relaxed max-w-2xl ${align === "center" ? "mx-auto" : ""}`}>
              {subtitle}
            </p>
          )}
          {(primary || secondary) && (
            <div className={`mt-9 flex flex-wrap gap-3 ${align === "center" ? "justify-center" : ""}`}>
              {primary &&
                (primary.to ? (
                  <Link
                    to={primary.to}
                    onClick={() => trackEvent("cta_click", { cta_label: primary.label, cta_location: "page_hero_primary" })}
                    className="bg-[var(--gold)] text-[var(--ink)] px-7 py-3.5 text-[11px] font-semibold uppercase tracking-[0.25em] hover:brightness-110 transition-all"
                  >
                    {primary.label}
                  </Link>
                ) : (
                  <a
                    href={primary.href}
                    onClick={() => trackEvent("cta_click", { cta_label: primary.label, cta_location: "page_hero_primary" })}
                    className="bg-[var(--gold)] text-[var(--ink)] px-7 py-3.5 text-[11px] font-semibold uppercase tracking-[0.25em] hover:brightness-110 transition-all"
                  >
                    {primary.label}
                  </a>
                ))}
              {secondary &&
                (secondary.to ? (
                  <Link
                    to={secondary.to}
                    onClick={() => trackEvent("cta_click", { cta_label: secondary.label, cta_location: "page_hero_secondary" })}
                    className="border border-white/40 text-white px-7 py-3.5 text-[11px] font-semibold uppercase tracking-[0.25em] hover:border-[var(--gold)] hover:text-[var(--gold)] transition-colors"
                  >
                    {secondary.label}
                  </Link>
                ) : (
                  <a
                    href={secondary.href}
                    onClick={() => trackEvent("cta_click", { cta_label: secondary.label, cta_location: "page_hero_secondary" })}
                    className="border border-white/40 text-white px-7 py-3.5 text-[11px] font-semibold uppercase tracking-[0.25em] hover:border-[var(--gold)] hover:text-[var(--gold)] transition-colors"
                  >
                    {secondary.label}
                  </a>
                ))}

            </div>
          )}
        </div>
      </div>

      {/* Indicateur scroll discret — masqué s'il y a des CTAs pour éviter tout chevauchement sur mobile */}
      {!primary && !secondary && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 hidden sm:flex flex-col items-center gap-2 text-white/60">
          <span className="text-[9px] uppercase tracking-[0.4em]">Scroll</span>
          <span className="block w-px h-10 bg-gradient-to-b from-[var(--gold)] to-transparent" />
        </div>
      )}
    </section>
  );
}
